import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as HighchartsExporting from 'highcharts/modules/exporting';

// Initialize exporting module safely
if (typeof HighchartsExporting === 'function') {
    HighchartsExporting(Highcharts);
} else if (HighchartsExporting && typeof HighchartsExporting.default === 'function') {
    HighchartsExporting.default(Highcharts);
}
import api from '../utils/api';
import { FiClock, FiX, FiCalendar, FiArrowRight, FiDownload, FiEye, FiEyeOff } from 'react-icons/fi';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MissingStatusReport = ({ year, month, isHROrAdmin }) => {
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [selectedYear, setSelectedYear] = useState(year);
    const [selectedMonth, setSelectedMonth] = useState(month);
    const [visibilityFilter, setVisibilityFilter] = useState('visible');
    const [toggling, setToggling] = useState(false);

    const yearOptions = [];
    const startYear = 2026;
    const currentYr = new Date().getFullYear();
    // Start at 2026 and continue up to next year automatically
    for (let i = startYear; i <= Math.max(startYear + 5, currentYr + 1); i++) yearOptions.push(i);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const res = await api.get('/hrms/missing-status-report', {
                params: { year: selectedYear, month: selectedMonth }
            });
            if (res.data.status) {
                setReportData(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching missing status report:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleVisibility = async (employee) => {
        try {
            setToggling(true);
            const newFlag = employee.attendance_flag === 1 ? 0 : 1;
            const res = await api.post('/hrms/toggle-attendance-flag', {
                user_id: employee.user_id,
                flag: newFlag
            });

            if (res.data.status) {
                toast.success(`Employee ${newFlag === 1 ? 'visible' : 'hidden'} now`);
                // Update local state
                setReportData(prev => prev.map(emp =>
                    emp.user_id === employee.user_id
                        ? { ...emp, attendance_flag: newFlag }
                        : emp
                ));
                setSelectedEmployee(prev => prev && prev.user_id === employee.user_id
                    ? { ...prev, attendance_flag: newFlag }
                    : prev
                );
            }
        } catch (error) {
            console.error('Error toggling visibility:', error);
            toast.error('Failed to update visibility');
        } finally {
            setToggling(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!selectedEmployee) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Missing Reports');

        // Title
        worksheet.mergeCells('A1:C1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = `Missing Status Report - ${selectedEmployee.name}`;
        titleCell.font = { name: 'Arial', size: 14, bold: true };
        titleCell.alignment = { horizontal: 'center' };

        // Employee Info
        worksheet.mergeCells('A2:C2');
        worksheet.getCell('A2').value = `Month: ${monthNames[selectedMonth - 1]} ${selectedYear}`;
        worksheet.getCell('A2').alignment = { horizontal: 'center' };

        worksheet.addRow([]);

        // Header
        worksheet.addRow(['Employee ID', 'Missing Date', 'Day']);
        const headerRow = worksheet.getRow(4);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFEEEEEE' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Data Rows
        selectedEmployee.details.forEach(detail => {
            const dateObj = new Date(detail.date);
            worksheet.addRow([
                selectedEmployee.user_id,
                detail.date,
                dateObj.toLocaleDateString('en-GB', { weekday: 'long' })
            ]);
        });

        // Summary Table
        worksheet.addRow([]);
        worksheet.addRow([]);

        const summaryHeaderRow = worksheet.addRow(['Summary', 'Count']);
        summaryHeaderRow.font = { bold: true };
        summaryHeaderRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };
        summaryHeaderRow.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };

        worksheet.addRow(['Total Missing Days', selectedEmployee.missed_count]);

        // Auto-width columns
        worksheet.columns.forEach(column => {
            column.width = 20;
        });

        // Generate Buffer
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Missing_Report_${selectedEmployee.name}_${selectedMonth}_${selectedYear}.xlsx`);
    };

    const handleDownloadAllExcel = async () => {
        if (!filteredData || filteredData.length === 0) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('All Missing Reports');

        // 1. Company Name (Row 1)
        worksheet.mergeCells('A1:D1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = 'Enova Software and Hardware Solutions P Ltd.';
        companyCell.font = { name: 'Calibri', size: 18, bold: true };
        companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 35; // Increased height
        worksheet.getRow(2).height = 25;

        // 2. Report Title (Row 2) - Red Header
        worksheet.mergeCells('A2:D2');
        const subHeaderCell = worksheet.getCell('A2');
        subHeaderCell.value = `Consolidated Missing Status Report - ${monthNames[selectedMonth - 1]} ${selectedYear}`;
        subHeaderCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; // White text
        subHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC00000' } }; // Dark Red background
        subHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };

        // 3. Table Headers (Dark Blue)
        const headerRow = worksheet.addRow(['Employee ID', 'Employee Name', 'Missed Count', 'Missing Dates']);
        headerRow.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } }; // White text
        headerRow.height = 25;

        ['A', 'B', 'C', 'D'].forEach(col => {
            const cell = worksheet.getCell(`${col}3`);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF232F3E' } }; // Dark Blue/Black
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            if (col === 'B' || col === 'D') cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        });

        // 4. Data Rows with Zebra Striping
        let totalMisses = 0;
        filteredData.forEach((emp, index) => {
            const dates = emp.details.map(d => d.date).join(', ');
            const row = worksheet.addRow([emp.user_id, emp.name, emp.missed_count, dates]);

            totalMisses += emp.missed_count;

            // Alternating Row Color (Light Pink)
            if (index % 2 !== 0) {
                row.eachCell((cell) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEBEB' } }; // Light Pink
                });
            }

            // Styling
            row.height = 30; // Taller rows
            row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' }; // ID
            row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }; // Name
            row.getCell(2).font = { name: 'Calibri', size: 11 };

            // Count - Red & Bold
            const countCell = row.getCell(3);
            countCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFF0000' } }; // Red
            countCell.alignment = { horizontal: 'center', vertical: 'middle' };

            // Dates
            row.getCell(4).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true, indent: 1 };
            row.getCell(4).font = { name: 'Calibri', size: 10 };
        });

        // 5. Total Row
        const totalRow = worksheet.addRow(['', 'TOTAL', totalMisses, '']);
        totalRow.height = 25;

        // Style Total Row (Dark Blue like header)
        ['A', 'B', 'C', 'D'].forEach(col => {
            const cell = totalRow.getCell(col);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF232F3E' } };
            cell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        // Merge A & B for "TOTAL" text
        const trNum = totalRow.number;
        worksheet.mergeCells(`A${trNum}:B${trNum}`);
        totalRow.getCell(1).value = 'TOTAL';

        // 6. Borders for entire table
        const lastRow = totalRow.number;
        for (let r = 3; r <= lastRow; r++) {
            const row = worksheet.getRow(r);
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFDDDDDD' } }, // Light gray borders
                    bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } }
                };
            });
        }

        // 7. Column Widths
        worksheet.columns = [
            { width: 15 }, // ID
            { width: 30 }, // Name
            { width: 15 }, // Count
            { width: 80 }  // Dates
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Enova_Consolidated_Missing_Report_${selectedMonth}_${selectedYear}.xlsx`);
    };


    useEffect(() => {
        if (isHROrAdmin) {
            fetchReport();
        }
    }, [selectedYear, selectedMonth, isHROrAdmin]);

    // Sync with props when they change (e.g. from global filters)
    useEffect(() => { setSelectedYear(year); }, [year]);
    useEffect(() => { setSelectedMonth(month); }, [month]);

    const filteredData = reportData.filter(d => visibilityFilter === 'visible' ? d.attendance_flag === 1 : d.attendance_flag === 0);

    if (!isHROrAdmin) return null;

    const chartOptions = {
        exporting: {
            enabled: true,
            sourceWidth: 1200,
            chartOptions: {
                chart: { backgroundColor: '#ffffff' },
                title: {
                    text: 'Missed status count (this month) until yesterday',
                    style: { fontSize: '20px', fontWeight: 'bold', color: '#1f2937', fontFamily: 'Arial' }
                }
            }
        },
        chart: {
            type: 'column',
            height: 300,
            backgroundColor: 'transparent',
            spacingTop: 50, // More space for avatars
            spacingBottom: 20,
            scrollablePlotArea: {
                minWidth: 700, // Force horizontal scroll on mobile
                scrollPositionX: 0
            }
        },
        title: { text: null },
        xAxis: {
            categories: filteredData.map(d => d.name),
            title: {
                text: 'Employee Name (Click names for details)',
                style: { fontSize: '9px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' },
                margin: 20
            },
            labels: {
                step: 1, // Show all labels regardless of space
                style: { fontSize: '11px', fontWeight: 'bold', color: '#64748b', cursor: 'pointer' },
                formatter: function () {
                    return this.value.split(' ')[0]; // Show first name only
                },
                events: {
                    click: function () {
                        const empName = this.value;
                        const emp = filteredData.find(d => d.name === empName);
                        if (emp) {
                            setSelectedEmployee(emp);
                            setShowModal(true);
                        }
                    }
                }
            },
            lineWidth: 1,
            lineColor: '#e2e8f0',
            tickWidth: 0
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Count',
                style: { fontSize: '9px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }
            },
            gridLineWidth: 0, // No horizontal lines
            labels: { enabled: false }, // Hide Y scale labels
            tickInterval: 1
        },
        tooltip: {
            useHTML: true,
            outside: true, // Prevent overlap with avatars
            padding: 0,
            backgroundColor: 'transparent',
            borderWidth: 0,
            shadow: false,
            formatter: function () {
                const emp = filteredData[this.point.index];
                // Show only dates (backend now sends only dates in details)
                const missedDatesPreview = emp.details.slice(0, 3).map(d => `<li>${d.date}</li>`).join('');
                const moreText = emp.details.length > 3 ? `<li>...and ${emp.details.length - 3} more</li>` : '';

                return `
                <div style="padding: 12px; background: white; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); min-width: 150px;">
                    <b style="font-size: 13px; color: #1e293b; display: block; margin-bottom: 4px;">${emp.name}</b>
                    <div style="color: #ef4444; font-weight: 800; font-size: 11px; margin-bottom: 8px;">
                        ${this.y} Missed Submissions
                    </div>
                    <ul style="margin: 0; padding-left: 12px; font-size: 10px; color: #64748b; list-style-type: disc;">
                        ${missedDatesPreview}
                        ${moreText}
                    </ul>
                </div>
            `;
            }
        },
        plotOptions: {
            column: {
                borderRadius: 8, // Rounded top corners
                colorByPoint: true,
                colors: ['#7dd3fc', '#fcd34d', '#f87171', '#67e8f9', '#a78bfa', '#fb923c', '#4ade80'],
                pointPadding: 0.15,
                groupPadding: 0.1,
                borderWidth: 0,
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            const emp = filteredData[this.index];
                            setSelectedEmployee(emp);
                            setShowModal(true);
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    crop: false,
                    overflow: 'allow',
                    allowOverlap: true,
                    formatter: function () {
                        const emp = filteredData[this.point.index];
                        let avatarUrl = null;

                        if (emp?.profile_image) {
                            if (emp.profile_image.startsWith('http')) {
                                avatarUrl = emp.profile_image;
                            } else {
                                const basePath = emp.profile_image.startsWith('uploads/') ? '' : 'uploads/profiles/';
                                avatarUrl = `${process.env.REACT_APP_API_URL || ''}/${basePath}${emp.profile_image}`;
                            }
                        }

                        avatarUrl = avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random`;

                        return `
                        <div style="text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 32px; height: 32px; position: relative; top: -10px;">
                            <div style="width: 28px; height: 28px; border-radius: 50%; border: 2px solid white; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; background: #fff; display: flex; align-items: center; justify-content: center;">
                                <img src="${avatarUrl}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.src='https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random'" />
                            </div>
                            <div style="background: white; color: #475569; font-size: 9px; font-weight: 900; padding: 1px 4px; border-radius: 4px; position: absolute; bottom: -8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); border: 1px solid #f1f5f9;">${this.y}</div>
                        </div>
                    `;
                    },
                    verticalAlign: 'top',
                    inside: false,
                    padding: 0,
                    y: -45 // Position above the bar
                }
            }
        },
        series: [{
            name: 'Missed Days',
            data: filteredData.map(d => ({
                y: d.missed_count,
                color: d.attendance_flag === 0 ? '#cbd5e1' : undefined // Grey out if hidden
            })),
            showInLegend: false
        }],
        credits: { enabled: false }
    };

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition border border-gray-100 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 rounded-xl text-red-500 shadow-sm border border-red-100">
                        <FiClock size={20} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-black text-gray-900 leading-tight">Missed status count (this month) until yesterday</h3>
                            <button
                                onClick={handleDownloadAllExcel}
                                title="Download All Data Excel"
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition shadow-sm"
                            >
                                <FiDownload size={14} />
                                <span>Export</span>
                            </button>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{monthNames[selectedMonth - 1]} {selectedYear}</p>
                        </div>
                    </div>
                </div>

                {/* Local Filters */}
                <div className="flex items-center gap-2">
                    <select
                        value={visibilityFilter}
                        onChange={(e) => setVisibilityFilter(e.target.value)}
                        className="text-[10px] font-black uppercase text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-200 transition-all cursor-pointer pr-8"
                    >
                        <option value="visible">Visible</option>
                        <option value="hidden">Hidden</option>
                    </select>
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        className="text-xs font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-200 transition-all cursor-pointer pr-8"
                    >
                        {monthNames.map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                        ))}
                    </select>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="text-xs font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-200 transition-all cursor-pointer pr-8"
                    >
                        {yearOptions.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex-1 relative min-h-[300px] mt-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                    </div>
                ) : filteredData.length > 0 ? (
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <div className="bg-gray-50 p-6 rounded-full mb-4 border border-gray-100">
                            <FiClock className="text-5xl text-gray-200" />
                        </div>
                        <p className="font-black text-gray-400 uppercase tracking-widest text-xs">All caught up!</p>
                        <p className="text-xs text-gray-300 mt-1">No missing status reports for this period.</p>
                    </div>
                )}
            </div>

            {/* Compact Details Modal */}
            {showModal && selectedEmployee && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-[60] p-4 group">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-[320px] w-full overflow-hidden animate-in fade-in zoom-in duration-200 relative">
                        {/* Cross Icon - Absolute Top Right */}
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all z-10"
                        >
                            <FiX size={20} />
                        </button>

                        {/* Header with Profile */}
                        <div className="p-6 pb-4 flex flex-col items-center text-center mt-2">
                            <div className="relative mb-3">
                                <div className="w-20 h-20 rounded-full border-4 border-red-50 shadow-md overflow-hidden bg-gray-50">
                                    <img
                                        src={selectedEmployee.profile_image?.startsWith('http')
                                            ? selectedEmployee.profile_image
                                            : `${process.env.REACT_APP_API_URL || ''}/${selectedEmployee.profile_image?.startsWith('uploads/') ? '' : 'uploads/profiles/'}${selectedEmployee.profile_image}`}
                                        className="w-full h-full object-cover"
                                        alt=""
                                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedEmployee.name)}&background=random`; }}
                                    />
                                </div>
                                <div className="absolute -bottom-1 -right-1 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full border-2 border-white shadow-sm">
                                    {selectedEmployee.missed_count}
                                </div>
                            </div>

                            <h4 className="text-lg font-black text-gray-900 leading-tight">{selectedEmployee.name}</h4>
                            <div className="flex items-center gap-3 mt-1">
                                <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest">Missing Report Submissions</p>
                                <div className="relative group flex items-center justify-center">
                                    <button
                                        onClick={() => handleToggleVisibility(selectedEmployee)}
                                        disabled={toggling}
                                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-black transition-all ${selectedEmployee.attendance_flag === 1
                                            ? 'bg-green-50 border-green-200 text-green-600 shadow-sm'
                                            : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                                    >
                                        {selectedEmployee.attendance_flag === 1 ? <FiEye size={10} /> : <FiEyeOff size={10} />}
                                        {selectedEmployee.attendance_flag === 1 ? 'VISIBLE' : 'HIDDEN'}
                                    </button>
                                    {/* Custom Tooltip */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 hidden group-hover:block z-50 w-max pointer-events-none animate-in fade-in zoom-in duration-200">
                                        <div className="bg-gray-900 text-white text-[10px] font-medium py-1.5 px-3 rounded-lg shadow-xl relative">
                                            {selectedEmployee.attendance_flag === 1 ? "Click to Hide" : "Click to Visible"}
                                            {/* Arrow */}
                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -mb-[1px] border-4 border-transparent border-b-gray-900"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Date List */}
                        <div className="px-6 pb-6 mt-2">
                            <div className="bg-gray-50 rounded-2xl p-4 max-h-[220px] overflow-y-auto">
                                <div className="space-y-2">
                                    {selectedEmployee.details.map((detail, idx) => (
                                        <div key={idx} className="flex items-center justify-between py-2 px-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-red-50 flex flex-col items-center justify-center text-red-500">
                                                    <span className="text-[8px] font-black leading-none uppercase">{new Date(detail.date).toLocaleDateString('en-GB', { month: 'short' })}</span>
                                                    <span className="text-xs font-black leading-none">{new Date(detail.date).getDate()}</span>
                                                </div>
                                                <span className="text-xs font-bold text-gray-600">{new Date(detail.date).toLocaleDateString('en-GB', { weekday: 'long' })}</span>
                                            </div>
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <button
                                onClick={handleDownloadExcel}
                                className="w-full mt-5 py-3 bg-gray-900 text-white rounded-2xl text-xs font-black hover:bg-black transition-all shadow-md active:scale-95 flex items-center justify-center gap-2"
                            >
                                <FiDownload size={16} />
                                DOWNLOAD REPORT
                            </button>
                        </div>
                    </div>
                </div>

            )}
        </div>
    );
};

export default MissingStatusReport;
